import type { MaritalStatus } from '../spouseSection/spouseSectionTypes';
import { parseIncomeYen } from '../spouseSection/spouseEligibility';
import type { SpecialStatusSnapshot, SpecialStatusStep } from './specialStatusTypes';
import { emptySpecialStatus, SPECIAL_STATUS_STEP_ORDER } from './specialStatusTypes';

export function stepQuestionKey(step: SpecialStatusStep): string {
  return `submitDocuments.dependentDeduction.specialStatus.steps.${step}`;
}

export function stepIndex(step: SpecialStatusStep): number {
  return SPECIAL_STATUS_STEP_ORDER.indexOf(step);
}

export function isMarried(maritalStatus: MaritalStatus | ''): boolean {
  return maritalStatus === 'married';
}

export function nextStepAfter(
  current: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): SpecialStatusStep | null {
  const askTransfer = Boolean(opts.needsDisabilityWsTransferDate) || snap.workingStudentOutcome === 'eligible';
  switch (current) {
    case 'singleParentIntro':
      if (isMarried(maritalStatus)) return 'singleParentResult';
      return 'singleParentConfirmChild';
    case 'singleParentPickChild':
      return 'singleParentConfirmChild';
    case 'singleParentConfirmChild':
      if (snap.singleParentChildConfirmed !== 'yes') return 'singleParentResult';
      return 'singleParentClaimedByOther';
    case 'singleParentClaimedByOther':
      return 'singleParentResult';
    case 'singleParentResult':
      return 'widowIntro';
    case 'widowIntro':
      if (isMarried(maritalStatus)) return 'widowResult';
      return 'widowLossStatus';
    case 'widowLossStatus':
      if (snap.widowSpouseLost === 'no') return 'widowResult';
      return 'widowRemarried';
    case 'widowRemarried':
      return 'widowResult';
    case 'widowResult':
      return 'workingStudentIntro';
    case 'workingStudentIntro':
      return 'workingStudentEnrolled';
    case 'workingStudentEnrolled':
      if (snap.workingStudentEnrolled !== 'yes') return 'workingStudentResult';
      return 'workingStudentSchoolType';
    case 'workingStudentSchoolType':
      return 'workingStudentSalaryOnly';
    case 'workingStudentSalaryOnly':
      if (snap.workingStudentSalaryOnly === 'yes') return 'workingStudentSalaryRevenue';
      return 'workingStudentNonEmployment';
    case 'workingStudentSalaryRevenue':
      return 'workingStudentNonEmployment';
    case 'workingStudentNonEmployment':
      if (snap.workingStudentHasNonEmploymentIncome === 'yes') {
        return 'workingStudentNonEmploymentAmount';
      }
      return 'workingStudentResult';
    case 'workingStudentNonEmploymentAmount':
      return 'workingStudentResult';
    case 'workingStudentResult':
      return askTransfer ? 'disabilityWsChangedDuringYear' : 'review';
    case 'disabilityWsChangedDuringYear':
      return snap.disabilityWsChangedDuringYear === 'yes' ? 'disabilityWsChangeDate' : 'review';
    case 'disabilityWsChangeDate':
      return 'review';
    case 'review':
      return null;
    default:
      return null;
  }
}

export function firstStep(maritalStatus: MaritalStatus | ''): SpecialStatusStep {
  return 'singleParentIntro';
}

export function reviewStep(): SpecialStatusStep {
  return 'review';
}

export function isSpecialStatusComplete(step: SpecialStatusStep): boolean {
  return step === 'review';
}

export function isSkippedStep(
  step: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts?: { needsDisabilityWsTransferDate?: boolean }
): boolean {
  if (isMarried(maritalStatus)) {
    if (
      step === 'singleParentPickChild' ||
      step === 'singleParentConfirmChild' ||
      step === 'singleParentClaimedByOther' ||
      step === 'widowLossStatus' ||
      step === 'widowRemarried'
    ) {
      return true;
    }
  }
  if (step === 'singleParentPickChild' && snap.singleParentChildEmpFamilyId != null) {
    // shown only when multiple candidates at routing time
  }
  if (step === 'singleParentClaimedByOther' && snap.singleParentChildConfirmed !== 'yes') return true;
  if (step === 'widowRemarried' && (snap.widowSpouseLost === 'no' || snap.widowSpouseLost === '')) return true;
  if (step === 'workingStudentSalaryRevenue' && snap.workingStudentSalaryOnly !== 'yes') return true;
  if (step === 'workingStudentNonEmploymentAmount' && snap.workingStudentHasNonEmploymentIncome !== 'yes') {
    return true;
  }
  if (
    step === 'workingStudentSchoolType' ||
    step === 'workingStudentSalaryOnly' ||
    step === 'workingStudentSalaryRevenue' ||
    step === 'workingStudentNonEmployment' ||
    step === 'workingStudentNonEmploymentAmount'
  ) {
    if (snap.workingStudentEnrolled !== 'yes') return true;
  }
  const askTransfer =
    Boolean(opts?.needsDisabilityWsTransferDate) || snap.workingStudentOutcome === 'eligible';
  if (step === 'disabilityWsChangedDuringYear' || step === 'disabilityWsChangeDate') {
    if (!askTransfer) return true;
  }
  if (step === 'disabilityWsChangeDate' && snap.disabilityWsChangedDuringYear !== 'yes') return true;
  return false;
}

export function shouldShowStepInHistory(
  step: SpecialStatusStep,
  committed: boolean,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount = 1,
  needsDisabilityWsTransferDate = false
): boolean {
  if (step === 'singleParentPickChild') return false;
  if (
    (step === 'singleParentConfirmChild' || step === 'singleParentClaimedByOther') &&
    candidateCount > 1
  ) {
    return false;
  }
  return committed && !isSkippedStep(step, snap, maritalStatus, { needsDisabilityWsTransferDate });
}

/** Step to reopen after undoing `undoFrom` (clears that answer and downstream). */
export function landOnStepAfterUndo(
  undoFrom: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): SpecialStatusStep | null {
  const prev = prevStepForUndo(undoFrom, snap, maritalStatus, opts);
  if (undoFrom.endsWith('Result')) return prev;
  return undoFrom;
}

/** Clear snapshot fields for `undoFrom` and every step after it on the active path. */
export function clearFromStepInclusive(
  undoFrom: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): Partial<SpecialStatusSnapshot> {
  const prev = prevStepForUndo(undoFrom, snap, maritalStatus, opts);
  if (prev) return clearDownstreamFrom(prev);
  const empty = emptySpecialStatus();
  const patch: Partial<SpecialStatusSnapshot> = {};
  const fields = STEP_ANSWER_FIELDS[undoFrom];
  if (fields) {
    for (const field of fields) {
      (patch as Record<string, unknown>)[field] = empty[field];
    }
  }
  return patch;
}

export function prevStepForUndo(
  current: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): SpecialStatusStep | null {
  const idx = stepIndex(current);
  if (idx <= 0) return null;
  for (let i = idx - 1; i >= 0; i--) {
    const candidate = SPECIAL_STATUS_STEP_ORDER[i];
    if (candidate === 'singleParentPickChild') continue;
    if (
      !isSkippedStep(candidate, snap, maritalStatus, {
        needsDisabilityWsTransferDate: opts.needsDisabilityWsTransferDate,
      })
    ) {
      return candidate;
    }
  }
  return null;
}

export function undoSourceStepForCurrent(
  current: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): SpecialStatusStep | null {
  if (current === 'review') return null;
  let probe = prevStepForUndo(current, snap, maritalStatus, opts);
  while (probe && probe.endsWith('Intro')) {
    probe = prevStepForUndo(probe, snap, maritalStatus, opts);
  }
  return probe;
}

export function undoTargetStep(
  current: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  committed: Set<SpecialStatusStep>,
  reviewMode: boolean,
  opts: { candidateCount: number; needsDisabilityWsTransferDate?: boolean }
): SpecialStatusStep | null {
  if (reviewMode) {
    return lastAnsweredStep(
      snap,
      maritalStatus,
      opts.candidateCount,
      Boolean(opts.needsDisabilityWsTransferDate)
    );
  }
  if (current.endsWith('Intro') || current === 'review') return null;

  const curIdx = stepIndex(current);
  let best: SpecialStatusStep | null = null;
  let bestIdx = -1;
  for (const s of SPECIAL_STATUS_STEP_ORDER) {
    if (s === 'review' || s.endsWith('Intro')) continue;
    if (!committed.has(s)) continue;
    if (
      isSkippedStep(s, snap, maritalStatus, {
        needsDisabilityWsTransferDate: opts.needsDisabilityWsTransferDate,
      })
    ) {
      continue;
    }
    if (
      !shouldShowStepInHistory(
        s,
        true,
        snap,
        maritalStatus,
        opts.candidateCount,
        Boolean(opts.needsDisabilityWsTransferDate)
      )
    ) {
      continue;
    }
    const idx = stepIndex(s);
    if (idx < curIdx && idx > bestIdx) {
      bestIdx = idx;
      best = s;
    }
  }
  return best;
}

export function showUndoForStep(
  step: SpecialStatusStep,
  current: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  committed: boolean,
  reviewMode: boolean,
  opts: {
    candidateCount: number;
    allCommitted: Set<SpecialStatusStep>;
    needsDisabilityWsTransferDate?: boolean;
  }
): boolean {
  if (!committed || step.endsWith('Intro')) return false;
  return (
    undoTargetStep(current, snap, maritalStatus, opts.allCommitted, reviewMode, {
      candidateCount: opts.candidateCount,
      needsDisabilityWsTransferDate: opts.needsDisabilityWsTransferDate,
    }) === step
  );
}

/** Last non-intro answer step in the completed path (for review-mode undo). */
export function lastAnsweredStep(
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount: number,
  needsDisabilityWsTransferDate = false
): SpecialStatusStep | null {
  let last: SpecialStatusStep | null = null;
  for (const s of SPECIAL_STATUS_STEP_ORDER) {
    if (s === 'review') break;
    if (s.endsWith('Intro') || isSkippedStep(s, snap, maritalStatus, { needsDisabilityWsTransferDate })) continue;
    if (isStepAnswered(s, snap, maritalStatus, candidateCount)) {
      last = s;
    }
  }
  return last;
}

export function specialStatusCursor(step: SpecialStatusStep): string {
  return `specialStatus:${step}`;
}

export function parseSpecialStatusCursor(currentStepId: string | null | undefined): SpecialStatusStep | null {
  const m = (currentStepId ?? '').match(/^specialStatus:(.+)$/);
  if (!m) return null;
  const step = m[1] as SpecialStatusStep;
  return SPECIAL_STATUS_STEP_ORDER.includes(step) ? step : null;
}

const STEP_ANSWER_FIELDS: Partial<Record<SpecialStatusStep, (keyof SpecialStatusSnapshot)[]>> = {
  singleParentPickChild: ['singleParentChildEmpFamilyId', 'singleParentChildRelationship'],
  singleParentConfirmChild: ['singleParentChildConfirmed'],
  singleParentClaimedByOther: ['singleParentClaimedByOther'],
  singleParentResult: ['singleParentOutcome'],
  widowLossStatus: ['widowSpouseLost'],
  widowRemarried: ['widowCurrentlyRemarried'],
  widowResult: ['widowOutcome'],
  workingStudentEnrolled: ['workingStudentEnrolled'],
  workingStudentSchoolType: ['workingStudentSchoolType'],
  workingStudentSalaryOnly: ['workingStudentSalaryOnly'],
  workingStudentSalaryRevenue: ['workingStudentSalaryRevenue'],
  workingStudentNonEmployment: ['workingStudentHasNonEmploymentIncome'],
  workingStudentNonEmploymentAmount: ['workingStudentNonEmploymentIncome'],
  workingStudentResult: ['workingStudentOutcome'],
  disabilityWsChangedDuringYear: ['disabilityWsChangedDuringYear'],
  disabilityWsChangeDate: ['disabilityWsChangeDate'],
};

/** Clear answers for all steps after `target` (used when undoing). */
export function clearDownstreamFrom(target: SpecialStatusStep): Partial<SpecialStatusSnapshot> {
  const empty = emptySpecialStatus();
  const targetIdx = stepIndex(target);
  const patch: Partial<SpecialStatusSnapshot> = {};

  for (const [step, fields] of Object.entries(STEP_ANSWER_FIELDS) as [
    SpecialStatusStep,
    (keyof SpecialStatusSnapshot)[],
  ][]) {
    if (stepIndex(step) > targetIdx) {
      for (const field of fields) {
        (patch as Record<string, unknown>)[field] = empty[field];
      }
    }
  }
  return patch;
}

export function isStepAnswered(
  step: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount: number
): boolean {
  switch (step) {
    case 'singleParentIntro':
    case 'widowIntro':
    case 'workingStudentIntro':
      return true;
    case 'singleParentPickChild':
      return snap.singleParentChildEmpFamilyId != null;
    case 'singleParentConfirmChild':
      return snap.singleParentChildConfirmed === 'yes' || snap.singleParentChildConfirmed === 'no';
    case 'singleParentClaimedByOther':
      return snap.singleParentClaimedByOther === 'yes' || snap.singleParentClaimedByOther === 'no';
    case 'singleParentResult':
      return snap.singleParentOutcome !== '';
    case 'widowLossStatus':
      return snap.widowSpouseLost !== '';
    case 'widowRemarried':
      return snap.widowCurrentlyRemarried === 'yes' || snap.widowCurrentlyRemarried === 'no';
    case 'widowResult':
      return snap.widowOutcome !== '';
    case 'workingStudentEnrolled':
      return snap.workingStudentEnrolled === 'yes' || snap.workingStudentEnrolled === 'no';
    case 'workingStudentSchoolType':
      return snap.workingStudentSchoolType !== '';
    case 'workingStudentSalaryOnly':
      return snap.workingStudentSalaryOnly === 'yes' || snap.workingStudentSalaryOnly === 'no';
    case 'workingStudentSalaryRevenue':
      return parseIncomeYen(snap.workingStudentSalaryRevenue) > 0;
    case 'workingStudentNonEmployment':
      return (
        snap.workingStudentHasNonEmploymentIncome === 'yes' ||
        snap.workingStudentHasNonEmploymentIncome === 'no'
      );
    case 'workingStudentNonEmploymentAmount':
      return parseIncomeYen(snap.workingStudentNonEmploymentIncome) > 0;
    case 'workingStudentResult':
      return snap.workingStudentOutcome !== '';
    case 'disabilityWsChangedDuringYear':
      return snap.disabilityWsChangedDuringYear === 'yes' || snap.disabilityWsChangedDuringYear === 'no';
    case 'disabilityWsChangeDate':
      return Boolean(snap.disabilityWsChangeDate.trim());
    default:
      return false;
  }
}

function hasOrphanAnswersAfter(
  from: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount: number,
  needsDisabilityWsTransferDate = false
): boolean {
  const fromIdx = stepIndex(from);
  for (const step of SPECIAL_STATUS_STEP_ORDER) {
    if (step === 'review') break;
    if (stepIndex(step) <= fromIdx) continue;
    if (isSkippedStep(step, snap, maritalStatus, { needsDisabilityWsTransferDate })) continue;
    if (step.endsWith('Intro')) continue;
    if (isStepAnswered(step, snap, maritalStatus, candidateCount)) return true;
  }
  return false;
}

export type SpecialStatusResumeState = {
  snap: SpecialStatusSnapshot;
  resumeStep: SpecialStatusStep;
  committed: Set<SpecialStatusStep>;
  canReview: boolean;
  needsPersist: boolean;
};

/** Derive resume step and committed history from saved snapshot (detects orphan DB data). */
export function buildResumeState(
  rawSnap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount: number,
  sectionDoneFlag: boolean,
  needsDisabilityWsTransferDate = false
): SpecialStatusResumeState {
  let snap = { ...rawSnap };
  const committed = new Set<SpecialStatusStep>();
  let step: SpecialStatusStep = firstStep(maritalStatus);
  const routeOpts = { candidateCount, needsDisabilityWsTransferDate };

  while (step !== 'review') {
    if (step.endsWith('Intro')) {
      committed.add(step);
      const next = nextStepAfter(step, snap, maritalStatus, routeOpts);
      if (!next) break;
      step = next;
      continue;
    }

    if (isSkippedStep(step, snap, maritalStatus, { needsDisabilityWsTransferDate })) {
      const next = nextStepAfter(step, snap, maritalStatus, routeOpts);
      if (!next) break;
      step = next;
      continue;
    }

    if (!isStepAnswered(step, snap, maritalStatus, candidateCount)) {
      const orphan = hasOrphanAnswersAfter(step, snap, maritalStatus, candidateCount, needsDisabilityWsTransferDate);
      if (orphan) {
        const patch = clearDownstreamFrom(step);
        snap = { ...snap, ...patch };
      }
      return {
        snap,
        resumeStep: step,
        committed,
        canReview: false,
        needsPersist: orphan || sectionDoneFlag,
      };
    }

    committed.add(step);
    const next = nextStepAfter(step, snap, maritalStatus, routeOpts);
    if (!next) break;
    step = next;
  }

  const askTransfer = needsDisabilityWsTransferDate || snap.workingStudentOutcome === 'eligible';
  const transferDone =
    !askTransfer ||
    (isStepAnswered('disabilityWsChangedDuringYear', snap, maritalStatus, candidateCount) &&
      (snap.disabilityWsChangedDuringYear !== 'yes' ||
        isStepAnswered('disabilityWsChangeDate', snap, maritalStatus, candidateCount)));

  const canReview =
    step === 'review' &&
    isStepAnswered('workingStudentResult', snap, maritalStatus, candidateCount) &&
    transferDone;

  if (sectionDoneFlag && !canReview) {
    const resumeStep =
      step === 'review' ? ('workingStudentIntro' as SpecialStatusStep) : step;
    return {
      snap,
      resumeStep,
      committed,
      canReview: false,
      needsPersist: true,
    };
  }

  return {
    snap,
    resumeStep: canReview ? 'review' : step,
    committed,
    canReview,
    needsPersist: false,
  };
}

export function hasDisplayableAnswer(
  step: SpecialStatusStep,
  snap: SpecialStatusSnapshot,
  maritalStatus: MaritalStatus | '',
  candidateCount: number
): boolean {
  if (step.endsWith('Intro')) return false;
  return isStepAnswered(step, snap, maritalStatus, candidateCount);
}
